/**
 * Home ウィジェットのデータ取得。
 *
 * アクセス権のあるものだけを並列で1回ずつ取得し、失敗したものは null のままにする。
 * ウィジェット単体の失敗で Home 全体が壊れないよう、個々に catch する。
 * PWA は開きっぱなしで再マウントされないため、表示が戻ったときに古ければ取り直す。
 */

import { format, isToday } from 'date-fns'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getActivityReviewProgress } from '@/api/services/activity-review'
import { getEufySummary } from '@/api/services/eufy'
import { getGarminDashboard } from '@/api/services/garmin'
import { getPickupSummary } from '@/api/services/library-favorite'
import { getNotifications, getRunningNotifications } from '@/api/services/notification'
import { getDailySummary } from '@/api/services/nutrition'
import { getRecentProducts } from '@/api/services/progress'
import { getReceiptsSummary } from '@/api/services/receipt'
import { listTodos } from '@/api/services/todo'
import { getSessions } from '@/api/services/workout'
import type { EufySummaryResponse } from '@/api/types/eufy'
import type { GarminDashboardResponse } from '@/api/types/garmin'
import type { NotificationLogRead } from '@/api/types/notification'
import type { DailyNutrition } from '@/api/types/nutrition'
import type { TodoResponse } from '@/api/types/todo'
import type { ProductProgress } from '@/api/types/user'
import type { WorkoutSessionListItem } from '@/api/types/workout'
import { parseUtcDate } from '@/lib/date-utils'
import { PATHS } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'

/** 当日のエラーを数えるために遡る通知ログの件数 */
const ERROR_LOG_SCAN_LIMIT = 100

/** 復帰のたびに Garmin へ問い合わせると重いので、この間隔を空けてから取り直す */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000

/** 履歴ウィジェットは 1 マス 1 枚なので、最大の 4x4 ぶん */
const RECENT_PRODUCTS_LIMIT = 16

/** 取得結果ではなく権限だけで決まる。レスポンス前でもグリッドの枚数を固定するために使う */
export type HomeWidgetPermissions = {
  todo: boolean
  nutrition: boolean
  activity: boolean
  workout: boolean
  content: boolean
  fullAccess: boolean
}

export type HomeWidgetsData = {
  isLoading: boolean
  todos: TodoResponse[] | null
  nutrition: DailyNutrition | null
  garmin: GarminDashboardResponse | null
  sessions: WorkoutSessionListItem[] | null
  weight: EufySummaryResponse | null
  unreviewedDays: number | null
  receiptPending: number | null
  /** 図書館で受け取れる予約本の冊数 */
  libraryPickupReady: number | null
  runningTasks: NotificationLogRead[] | null
  todayErrors: number | null
  recentProducts: ProductProgress[] | null
  permissions: HomeWidgetPermissions
  /** TODO だけ取り直す（クイック追加の後に使う。重い Garmin などは叩かない） */
  reloadTodos: () => Promise<void>
}

const INITIAL: Omit<HomeWidgetsData, 'permissions' | 'reloadTodos'> = {
  isLoading: true,
  todos: null,
  nutrition: null,
  garmin: null,
  sessions: null,
  weight: null,
  unreviewedDays: null,
  receiptPending: null,
  libraryPickupReady: null,
  runningTasks: null,
  todayErrors: null,
  recentProducts: null,
}

const optional = <T>(enabled: boolean, load: () => Promise<T>): Promise<T | null> =>
  enabled ? load().catch(() => null) : Promise.resolve(null)

const loadTodos = async (): Promise<TodoResponse[]> => {
  const todos = await listTodos({ include_done: false, limit: 100 })
  return todos.filter((todo) => !todo.is_event)
}

export const useHomeWidgets = (): HomeWidgetsData => {
  const accessiblePages = useAppStore((state) => state.accessiblePages)
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const canContent = useAppStore((state) => state.accessibleDomains.length > 0)

  const [data, setData] = useState(INITIAL)
  const lastLoadedAtRef = useRef(0)

  const paths = useMemo(() => new Set(accessiblePages.map((page) => page.path)), [accessiblePages])
  const hasPages = accessiblePages.length > 0
  const canTodo = paths.has(PATHS.TODO.HOME)
  const canNutrition = paths.has(PATHS.NUTRITION.HOME)
  const canActivity = paths.has(PATHS.ACTIVITY)
  const canWorkout = paths.has(PATHS.WORKOUT.HOME)
  const canReceipts = paths.has(PATHS.RECEIPTS.LIST)
  const canLibrary = paths.has(PATHS.LIBRARY.SEARCH)

  const load = useCallback(async (): Promise<Omit<HomeWidgetsData, 'permissions' | 'reloadTodos'>> => {
    const today = format(new Date(), 'yyyy-MM-dd')
    const [todos, nutrition, garmin, sessions, weight, review, receipts, running, errorLogs, recent, pickups] =
      await Promise.all([
        optional(canTodo, loadTodos),
        optional(canNutrition, () => getDailySummary(today)),
        optional(canActivity, () => getGarminDashboard(today)),
        optional(canWorkout, () => getSessions(30)),
        optional(canNutrition, () => getEufySummary()),
        optional(canActivity, () => getActivityReviewProgress()),
        optional(canReceipts, () => getReceiptsSummary({ review_status: 'needs_review' })),
        optional(hasFullAccess, () => getRunningNotifications()),
        optional(hasFullAccess, () => getNotifications({ has_errors: true, limit: ERROR_LOG_SCAN_LIMIT })),
        // 人目に触れる Home なので、復帰ダイアログと同じく Safe モードの対象は常に出さない
        optional(canContent, () => getRecentProducts(RECENT_PRODUCTS_LIMIT, true)),
        optional(canLibrary, () => getPickupSummary()),
      ])

    return {
      isLoading: false,
      todos,
      nutrition,
      garmin,
      sessions,
      weight,
      unreviewedDays: review?.unreviewed_days ?? null,
      receiptPending: receipts?.total_count ?? null,
      libraryPickupReady: pickups?.ready_count ?? null,
      runningTasks: running,
      todayErrors:
        errorLogs?.items
          .filter((log) => isToday(parseUtcDate(log.created_at)))
          .reduce((sum, log) => sum + log.failed_count, 0) ?? null,
      recentProducts: recent?.items ?? null,
    }
  }, [canTodo, canNutrition, canActivity, canWorkout, canReceipts, canContent, canLibrary, hasFullAccess])

  useEffect(() => {
    if (!hasPages) return
    let alive = true

    const run = async () => {
      lastLoadedAtRef.current = Date.now()
      const next = await load()
      if (alive) setData(next)
    }

    const refreshIfStale = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastLoadedAtRef.current < REFRESH_INTERVAL_MS) return
      void run()
    }

    void run()
    document.addEventListener('visibilitychange', refreshIfStale)
    return () => {
      alive = false
      document.removeEventListener('visibilitychange', refreshIfStale)
    }
  }, [hasPages, load])

  const permissions = useMemo<HomeWidgetPermissions>(
    () => ({
      todo: canTodo,
      nutrition: canNutrition,
      activity: canActivity,
      workout: canWorkout,
      content: canContent,
      fullAccess: hasFullAccess,
    }),
    [canTodo, canNutrition, canActivity, canWorkout, canContent, hasFullAccess],
  )

  const reloadTodos = useCallback(async () => {
    if (!canTodo) return
    const todos = await loadTodos().catch(() => null)
    if (todos === null) return
    setData((prev) => ({ ...prev, todos }))
  }, [canTodo])

  return { ...data, permissions, reloadTodos }
}
